import { useState } from 'react';
import { DefaultNamespace, TFunction } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BUTTON_VARIANTS } from '../../../constants/button-variants';
import ROUTES from '../../../constants/routes';
import useChangeFavorite from '../../../hooks/use-change-favorite';
import { world } from '../../../languages';
import { AVAILABLE_WORLDS } from '../../../languages/constants';
import { getDownloadHref } from '../../../services/downloads/get-download-href';
import cookies from '../../../utils/browser-storage/cookies';
import { storageKeys } from '../../../utils/browser-storage/storage-keys';
import interactiveRedirectHelper from '../../../utils/interactive/interactive-redirect-helper';
import isInteractiveStandaloneMaterial from '../../../utils/interactive/is-interactive-standalone-material';
import getFreeMaterial from '../../../utils/material/download-free-material';
import isInFavorites from '../../../utils/material/is-in-favorites';
import materialContainsDownloadableFiles from '../../../utils/material/material-contains-downloadable-files';
import navigateFreeThankYou from '../../../utils/material/navigate-free-thank-you';
import { requestFreeMaterial } from '../request-free-material';
import session from '../../../utils/session';
import { getUserData } from '../../../utils/user/user';
import { GetFreeMaterialButtonConfigProps } from '../get-free-material-button';

type UseFreeMaterialButtonConfigProps = {
    material: Material;
    t: TFunction<DefaultNamespace, undefined>;
    onModalClose: VoidFunction;
    isThankYouPage?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qualityEventsExtra?: any;
};

const useFreeMaterialBtnConfig = ({
    material,
    t,
    onModalClose,
    qualityEventsExtra
}: UseFreeMaterialButtonConfigProps): GetFreeMaterialButtonConfigProps => {
    const [showModal, setShowModal] = useState(false);
    const [showFreeModal, setShowFreeModal] = useState(false);
    const navigate = useNavigate();

    const favorite = useSelector((state: RootState) => state.main.favorite);
    const { changeFavoriteItem } = useChangeFavorite();
    const isInteractiveStandalone = isInteractiveStandaloneMaterial(material);
    const isLoggedIn = session.isLoggedIn();
    const isHybrid = !isInteractiveStandalone && Boolean(material.interactiveId);
    const materialId = material.id;
    const skipDownload = isInteractiveStandalone || isHybrid;
    const isBundle = material.isBundleInteractive;

    const createRecord = (): void => {
        requestFreeMaterial(materialId).then(() => setShowModal(true));
    };
    const removeFromFavorites = (): void => {
        if (isInFavorites(materialId, favorite)) {
            changeFavoriteItem({ item: material, add: false });
        }
    };

    const getMaterial = (): void => {
        if (isLoggedIn) {
            if (!isInteractiveStandalone && world === AVAILABLE_WORLDS.DE) {
                navigateFreeThankYou({ navigate, materialId });
            } else if (!materialContainsDownloadableFiles(material)) {
                createRecord();
            } else {
                getFreeMaterial(
                    materialId,
                    {
                        materialInfo: material,
                        skipDownload
                    },
                    qualityEventsExtra
                );
                setShowModal(true);
            }

            removeFromFavorites();
        } else if (!isLoggedIn && !isInteractiveStandalone) {
            setShowFreeModal(true);
        } else {
            cookies.setItem(storageKeys.general.redirectLink, window.location.href, 1);
            navigate(ROUTES.LOGIN.path);
        }
    };

    const hideModal = (): void => {
        onModalClose();

        if (window.location.pathname.indexOf(ROUTES.FAVORITES.path) >= 0) {
            window.location.reload();
        } else {
            setShowModal(false);
        }
    };
    const hideFreeModal = (): void => setShowFreeModal(false);

    let freeMaterialButtonConfig: GetFreeMaterialButtonConfigProps = {
        showModal,
        hideModal,
        showFreeModal,
        hideFreeModal,
        getMaterial,
        modalDescription: t('common.materialBlock.getFreeMaterial.modalDescription'),
        buttonText: t('common.materialBlock.getFreeMaterial.buttonText'),
        buttonConfig: [
            {
                action: () => hideModal(),
                className: `${BUTTON_VARIANTS.SECONDARY} cancel-modal`,
                text: t('common.materialBlock.getFreeMaterial.cancel')
            },
            {
                action: () => navigate(ROUTES.BOUGHT_MATERIALS.path),
                className: BUTTON_VARIANTS.YELLOW,
                text: t('common.materialBlock.getFreeMaterial.toMyMaterials')
            }
        ]
    };

    if (isInteractiveStandalone) {
        const interactiveShareButton = {
            action: () => window.locationReload(interactiveRedirectHelper.getShareMaterialLink(materialId)),
            className: BUTTON_VARIANTS.YELLOW,
            text: t('materialPage.priceBlock.fakeDoorTitleC')
        };
        const interactiveToMyInteractivesButton = {
            action: () => navigate(ROUTES.NEW_INTERACTIVE_MY_MATERIALS.path),
            className: `${BUTTON_VARIANTS.SECONDARY}`,
            text: t('header.interactive.myInteractives')
        };

        freeMaterialButtonConfig = {
            ...freeMaterialButtonConfig,
            modalClassName: 'with-equal-buttons',
            modalDescription: t('common.materialBlock.getFreeMaterial.interactiveModalDescription'),
            modalDescriptionLink: ROUTES.NEW_INTERACTIVE_MY_MATERIALS.path,
            buttonText: t('common.materialBlock.getFreeMaterial.interactiveButtonText'),
            buttonConfig: isBundle
                ? [interactiveToMyInteractivesButton]
                : [interactiveToMyInteractivesButton, interactiveShareButton]
        };
    }

    if (isHybrid) {
        freeMaterialButtonConfig = {
            ...freeMaterialButtonConfig,
            modalClassName: 'with-equal-buttons',
            modalDescription: t('common.materialBlock.getFreeMaterial.hybridModalDescription'),
            modalDescriptionLink: ROUTES.BOUGHT_MATERIALS.path,
            buttonText: t('common.materialBlock.getFreeMaterial.interactiveButtonText'),
            buttonConfig: [
                {
                    action: () => window.locationReload(interactiveRedirectHelper.getShareMaterialLink(materialId)),
                    className: `${BUTTON_VARIANTS.SECONDARY}`,
                    text: t('cabinetMaterials.purchasedMaterials.materialBlock.toInteractive')
                },
                {
                    action: async () => {
                        const email =
                            getUserData().profileDetails.invoiceDetails.clientEmail ||
                            window.storage.getItem(storageKeys.email.freeMaterialDownloadUserEmail) ||
                            '';

                        const r = await requestFreeMaterial(materialId, email);
                        const { token } = r.data.items;

                        if (!token) {
                            return;
                        }

                        getDownloadHref({ id: token, isFree: true });
                        hideModal();
                    },
                    className: BUTTON_VARIANTS.YELLOW,
                    text: t('cabinetMaterials.purchasedMaterials.materialBlock.downloadButton')
                }
            ]
        };
    }

    return freeMaterialButtonConfig;
};

export default useFreeMaterialBtnConfig;
